import { useEffect } from 'react';

import { globalTrackError, globalTrackEvent, useObservability } from 'platform/observability';

export type TrackedExperience =
  | 'checkout-reviewDetails' // checkout flow
  | 'prelimOffer-offerCard' // seller offer card
  | 'listWithCertaintyPrelimOffer-offerCard' // seller offer card
  | 'finalOffer-offerCard' // final offer card
  | 'buyerOverview-feedFilterSearchBar'; // buyer overview page

/**
 * WARNING!!!!:
 * DO NOT use this hook INSIDE a modal
 * DO NOT use this hook on a page where a modal typically pops opens on load (this is for modals where people typically spend a lot of time on)
 *
 * Why:
 * - We track the time to render from the last route change or initial load event.
 * - Modals make things tricky as we do not consider a modal opening or closing as a route change. In the future, we will consider tracking this as a start event when we have a provider than handles all modals.
 *
 * Now the info:
 * Allows us to track the time it takes for a component to render from a initial load or route change event - measured in milliseconds
 * Uses the performance API to:
 * 1. Create a performance mark at the start of the rendering
 * 2. Measure the time between the two marks with performance.measure (the initial load or route change time minus the start of rendering the component)
 * 3. Tracks the time to render as an observability trackEvent. This also passes in if this is from an 'initialLoad' or 'routeChange' event to help us understand the context of the time to render.
 * https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#how-loading-time-is-calculated
 * Event: time-to-render
 * Label: `productExperience-componentName` example: prelimOffer-offerCard
 * @param trackedExperience - tracker name should be made up of the product experience the component is shown on and the name of the component that we're tracking performance on
 *
 * The combination of the team, productExperience, and componentName should be unique to the experience shown
 * For example: if the component is shown on two different experiences but on the same path you can use an if/else statement to ensure you're uniquely tracking in this hook (just updated the productExperience to be more unique to what you want to track)
 */
export const useTrackTimeToRender = (
  trackedExperience: TrackedExperience,
  /**
   * Allows people to opt out of tracking if needed
   * Helpful for opting out in cases where the component that is being tracked lives in a modal or have long-standing modals that open on page load (see warning for more details)
   */
  optOutOfTracking?: boolean,
) => {
  const { trackEvent, trackError } = useObservability();

  // Ignore hook on the server
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (optOutOfTracking) {
      return;
    }
    const analyticsName = `${trackedExperience}`;
    const markAnalyticsName = `${analyticsName}-end`;

    // Gets the performance entries for the initial load and route change events
    const initialLoadEntries = performance.getEntriesByName('cake-initial-load-event') ?? [];
    const routeChangeEntries = performance.getEntriesByName('cake-route-change-event') ?? [];

    // Determine which view load type event happened last (initialLoad or routeChange)
    const lastInitialLoadEntry = initialLoadEntries[initialLoadEntries.length - 1];
    const lastRouteChangeEntry = routeChangeEntries[routeChangeEntries.length - 1];
    const latestViewLoadEntry =
      lastRouteChangeEntry?.startTime > lastInitialLoadEntry?.startTime
        ? lastRouteChangeEntry
        : lastInitialLoadEntry;

    // Don't re-track if no navigation/load has happened since the last render
    const ttuiEntries = performance.getEntriesByName(markAnalyticsName) ?? [];
    const lastTTUIEntry = ttuiEntries[ttuiEntries.length - 1];

    /**
     * If the component as re-rendered for ANY reason other than a full page load caused by a route change or initial load event, we should not track the time to render. We only care about the TTUI relative to
     */
    if (lastTTUIEntry?.startTime > latestViewLoadEntry?.startTime) {
      return;
    }

    // Marks when this specific component mounts
    performance.mark(markAnalyticsName);

    // https://docs.datadoghq.com/real_user_monitoring/browser/monitoring_page_performance/#how-loading-time-is-calculated
    if (latestViewLoadEntry) {
      let startTime;
      let viewLoadType: 'initialLoad' | 'routeChange';
      if (latestViewLoadEntry === lastRouteChangeEntry) {
        startTime = lastRouteChangeEntry.startTime;
        viewLoadType = 'routeChange';
      } else {
        startTime = lastInitialLoadEntry.startTime;
        viewLoadType = 'initialLoad';
      }
      // https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure
      const timeToRender = performance.measure(`${analyticsName}-render`, {
        start: startTime,
        end: markAnalyticsName,
      });
      trackEvent('time-to-render', analyticsName, {
        timeToRender: timeToRender.duration,
        renderStartTime: timeToRender.startTime,
        viewLoadType,
      });

      if (window.ENV.DEPLOY_ENV === 'development' || window.ENV.DEPLOY_ENV === 'test') {
        // eslint-disable-next-line no-console --  Only log TTUI metrics in development
        console.log(`[TTUI] ${analyticsName}: ${timeToRender.duration}ms`);
      }
    } else {
      trackError(
        'time-to-render-error',
        new Error(`Performance initial load or navigation entry not found for ${analyticsName}`),
      );
    }
  } catch (error) {
    // https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure#exceptions
    globalTrackError(
      'time-to-render-error',
      new Error(`Error tracking time to render for ${trackedExperience}`),
      { error },
    );
  }
};

/**
 * Creates a performance.mark on initial load
 * This allows us to accurately track time to render for components - since we use this as the start time for the time to render.
 * A performance mark is a high resolution timestamp marker in the browser's performance timeline.
 * https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark
 */
export const useTrackInitialLoad = () => {
  const { trackEvent } = useObservability();
  useEffect(() => {
    performance.clearMarks('cake-initial-load-event');
    const initialLoadMark = performance.mark('cake-initial-load-event');
    trackEvent('load-event', 'cake-initial-load-event', {
      markedStartTime: initialLoadMark.startTime,
      duration: initialLoadMark.duration,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only track initial load
  }, []);
};

/**
 * Creates a performance.mark on the route change event
 * This allows us to accurately track time to render for components - since we use this as the start time for the time to render.
 * A performance mark is a high resolution timestamp marker in the browser's performance timeline.
 * https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark
 */
export const trackNavigationEvent = () => {
  performance.clearMarks('cake-route-change-event');
  const navigationMark = performance.mark('cake-route-change-event');
  globalTrackEvent('navigation-event', 'cake-route-change-event', {
    renderStartTime: navigationMark.startTime,
    duration: navigationMark.duration,
  });
};

export const trackStateUpdateNavigationEvent = () => {
  performance.clearMarks('cake-route-change-event');
  const navigationMark = performance.mark('cake-route-change-event');
  globalTrackEvent('navigation-event', 'cake-state-change-event', {
    renderStartTime: navigationMark.startTime,
    duration: navigationMark.duration,
  });
};
